import 'simplebar/dist/simplebar.min.css';

import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { forwardRef, useEffect, useRef } from 'react';
import SimplebarCore from 'simplebar/dist/simplebar-core.esm';

const Scrollbar = forwardRef((props, ref) => {
  const elRef = useRef();
  const contentNodeRef = useRef();
  const scrollableNodeRef = useRef();
  const { children, onScroll, ...rest } = props;

  let instance;
  let otherProps = {};
  let options = {
    autoHide: false,
    scrollbarMinSize: 80,
  };

  Object.keys(rest).forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(SimplebarCore.defaultOptions, key)) {
      options[key] = rest[key];
    } else {
      otherProps[key] = rest[key];
    }
  });

  useEffect(() => {
    if (elRef.current) {
      instance = new SimplebarCore(elRef.current, {
        ...options,
        ...(scrollableNodeRef && {
          scrollableNode: scrollableNodeRef.current,
        }),
        ...(contentNodeRef.current && {
          contentNode: contentNodeRef.current,
        }),
      });

      if (ref) {
        ref.current = instance;
      }

      instance.getScrollElement().addEventListener('scroll', onScroll);
    }

    return () => {
      instance.unMount();
      instance.getScrollElement().removeEventListener('scroll', onScroll);

      instance = null;
    };
  }, []);

  return (
    <Box
      ref={elRef}
      className='simplebar-container'
      data-simplebar
      sx={(theme) => ({
        width: '100%',
        height: '100%',
        maxHeight: '100%',
        '.simplebar-vertical': {
          right: '0',
          width: theme.spacing(2),
        },
        '.simplebar-content': {
          minHeight: '100%',
        },
        '.simplebar-scrollbar': {
          '&:before': {
            right: '1px',
            top: '1px',
            bottom: '1px',
            backgroundColor: alpha(theme.palette.grey.main, 0.12),
          },
          '&.simplebar-hover:before': {
            backgroundColor: alpha(theme.palette.grey.main, 0.32),
          },
          '&.simplebar-visible:before': {
            opacity: '1',
          },
        },
        '.simplebar-track': {
          '&.simplebar-vertical': {
            width: '13px',
          },
          '&.simplebar-horizontal': {
            height: '13px',
            '.simplebar-scrollbar': {
              top: '1px',
              height: '10px',
            },
          },
        },
      })}
      {...otherProps}>
      <div className='simplebar-wrapper'>
        <div className='simplebar-height-auto-observer-wrapper'>
          <div className='simplebar-height-auto-observer' />
        </div>
        <div className='simplebar-mask'>
          <div className='simplebar-offset'>
            {typeof children === 'function' && children({ scrollableNodeRef, contentNodeRef })}
            {typeof children !== 'function' && (
              <div className='simplebar-content-wrapper'>
                <div className='simplebar-content'>{children}</div>
              </div>
            )}
          </div>
        </div>
        <div className='simplebar-placeholder' />
      </div>
      <div className='simplebar-track simplebar-horizontal'>
        <div className='simplebar-scrollbar' />
      </div>
      <div className='simplebar-track simplebar-vertical'>
        <div className='simplebar-scrollbar' />
      </div>
    </Box>
  );
});

export default Scrollbar;
